/*
 * Copyright (c) 2025. Bolmate B.V.
 */

import {ErrorType, ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import React, {ForwardedRef, forwardRef, useContext, useEffect, useImperativeHandle, useState} from 'react';
import BolProduct from '../../../../models/bolProductModel';
import {COUNTRIES} from '../../../../util/countryUtil';
import {ActivateHandle, VATType, VATValue} from '../../../../util/interfaces';
import {createErrorMessage} from '../../../../util/utils';
import {CustomVAT, DefaultVAT} from '../../../../util/vatUtil';
import {AppContext} from '../../../misc/appContext';
import ProductVAT from './productVAT';

interface Props {
    onSuccess: (product: BolProduct) => void;
}

function ProductVATController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const {account} = useContext(AppContext).accountContext;
    const {onSuccess} = props;
    const [active, setActive] = useState(false);
    const [product, setProduct] = useState(new BolProduct());
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, ErrorType>>({});
    const [availableCountries, setAvailableCountries] = useState<Array<[string, string]>>([]);

    function onActivate(product: BolProduct) {
        setProduct(product);
        setActive(true);
    }

    function onChangeTitle(value: string) {
        setProduct(product.clone().setField('title', value));
    }

    function onResetTitle() {
        setProduct(product.clone().setField('title', null));
    }

    function onClickChange(countryCode: string, value: VATValue) {
        return function() {
            onChange(countryCode)(value);
        };
    }

    function onChange(countryCode: string) {
        return function(value: VATValue | number) {
            const _product = product.clone();
            if (typeof value === 'number') value = CustomVAT(value);
            _product.vats.setCountryValue(countryCode, value);
            setProduct(_product);
            setErrors({...errors, [countryCode]: null});
        };
    }

    function onAddCountry(countryCode: string) {
        const _product = product.clone();
        _product.vats.setCountryValue(countryCode, DefaultVAT(0));
        setProduct(_product);
    }

    function onDeleteCountry(countryCode: string) {
        return function() {
            const _product = product.clone();
            _product.vats.deleteCountryValue(countryCode);
            setProduct(_product);
        };
    }

    function onChangeSplitPercentage(countryCode: string, oldPercentage: string) {
        return function(newPercentage: string) {
            const _product = product.clone();
            const currentValue = _product.vats.getCountryValue(countryCode);
            if (currentValue?.vat_type !== VATType.SPLIT) return;
            currentValue.value[newPercentage] = currentValue.value[oldPercentage];
            delete currentValue.value[oldPercentage];
            setProduct(_product);
        };
    }

    function onChangeSplitValue(countryCode: string, percentage: string) {
        return function(value: number) {
            const _product = product.clone();
            const currentValue = _product.vats.getCountryValue(countryCode);
            if (currentValue?.vat_type !== VATType.SPLIT) return;
            currentValue.value[percentage] = value;
            setProduct(_product);
        };
    }

    function onClose() {
        setActive(false);
        setProduct(new BolProduct());
        setLoading(false);
        setErrors({});
        setAvailableCountries([]);
    }

    function onSubmit(retroactive?: boolean) {
        return function() {
            if (!account || !validateValues()) return;
            if (retroactive === undefined) {
                const message = 'Wil je de gewijzigde BTW percentages met terugwerkende kracht toepassen? Hiermee worden ' +
                    'alle facturen met dit product bijgewerkt.';
                return showDialog(message, 'Wijzigingen toepassen',
                    [{label: 'Nee, alleen voor nieuwe facturen', onClick: onSubmit(false)},
                        {label: 'Ja, met terugwerkende kracht', onClick: onSubmit(true), primary: false}]
                );
            }
            setLoading(true);
            product.update(retroactive, (product: BolProduct, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    onSuccess(product);
                    onClose();
                    showSnackbar('De wijzigingen zijn opgeslagen');
                } else
                    showDialog(createErrorMessage(data.message ?? 'Het opslaan van de wijzigingen is niet gelukt.'),
                        'Opslaan mislukt');
            });
        };
    }

    function validateValues(): boolean {
        let valid = true;
        const _errors = {...errors};
        for (const [countryCode, vatValue] of product.vats.entries()) {
            if (![VATType.CUSTOM, VATType.SPLIT].includes(vatValue.vat_type)) continue;
            if (vatValue.vat_type === VATType.CUSTOM) {
                if (vatValue.value > 100) {
                    _errors[countryCode] = 'Het percentage kan niet meer zijn dan 100%';
                    valid = false;
                } else if (vatValue.value < 0) {
                    _errors[countryCode] = 'Het percentage kan niet negatief zijn';
                    valid = false;
                }
            } else if (vatValue.vat_type === VATType.SPLIT) {
                for (const value of Object.values(vatValue.value as Record<string, number>)) {
                    if (value < 0) {
                        _errors[countryCode] = 'Het BTW bedrag kan niet negatief zijn';
                        valid = false;
                    }
                }
            }
        }
        if (!valid) setErrors(_errors);
        return valid;
    }

    useEffect(() => {
        const usedCountryCodes = Array.from(product.vats.keys());
        setAvailableCountries(Object.entries(COUNTRIES).filter((entry) => !usedCountryCodes.includes(entry[0])));
    }, [product.vats]);

    useImperativeHandle(ref, () => ({activate: onActivate}));

    return (
        <ProductVAT onClose={onClose} onSubmit={onSubmit()} onChangeTitle={onChangeTitle} onClickChange={onClickChange}
                    onChange={onChange} onAddCountry={onAddCountry} onDeleteCountry={onDeleteCountry}
                    onChangeSplitPercentage={onChangeSplitPercentage} onChangeSplitValue={onChangeSplitValue}
                    onResetTitle={onResetTitle} product={product} loading={loading} active={active} errors={errors}
                    availableCountries={availableCountries}/>
    );
}

export default forwardRef<ActivateHandle, Props>(ProductVATController);